import { useState } from 'react';
import { Sidebar } from '@/components/layout/Sidebar';
import { useAuth } from '@/lib/auth';
import { innoflashes, categoryConfig, getAllTags, InnoFlash } from '@/data/innoflashes';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, Calendar, User, FileText, Filter } from 'lucide-react';
import { InnoFlashModal } from '@/components/innoflashes/InnoFlashModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const palette = {
  background: '#F5F9ED',
  border: '#DAE9B1',
  accent: '#A5CE39',
  accentDark: '#7A9C2F',
};

export default function InnoFlashes() {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'title'>('date-desc');
  const [selectedFlash, setSelectedFlash] = useState<InnoFlash | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const allTags = getAllTags();

  const handleFlashClick = (flash: InnoFlash) => {
    setSelectedFlash(flash);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedFlash(null), 300); // Clear after animation
  };

  // Filter InnoFlashes
  const filteredFlashes = innoflashes.filter((flash) => {
    const categoryMatch = selectedCategory === 'all' || flash.category === selectedCategory;
    const tagMatch = selectedTag === 'all' || flash.tags.includes(selectedTag);
    return categoryMatch && tagMatch;
  });

  // Sort InnoFlashes
  const sortedFlashes = [...filteredFlashes].sort((a, b) => {
    switch (sortBy) {
      case 'date-desc':
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      case 'date-asc':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  const InnoFlashCard = ({ flash }: { flash: InnoFlash }) => {
    const categoryStyle = categoryConfig[flash.category];

    return (
      <div
        className="bg-white rounded-xl p-6 transition-all duration-200 ease-out h-full cursor-pointer group"
        style={{
          border: `1px solid ${palette.border}`,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        }}
        onClick={() => handleFlashClick(flash)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.borderColor = categoryStyle.color;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
          e.currentTarget.style.borderColor = palette.border;
        }}
      >
        {/* Preview Image or Icon */}
        <div
          className="w-full h-40 rounded-lg flex items-center justify-center mb-4 overflow-hidden"
          style={{ backgroundColor: categoryStyle.bgColor }}
        >
          {flash.previewImage ? (
            <img
              src={flash.previewImage}
              alt={flash.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to icon if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : (
            <div />
          )}
          <Lightbulb
            size={48}
            style={{ color: categoryStyle.color }}
            className={flash.previewImage ? 'hidden' : ''}
          />
        </div>

        {/* Category Badge */}
        <div className="mb-3">
          <Badge
            className="text-xs"
            style={{
              backgroundColor: categoryStyle.bgColor,
              color: categoryStyle.color,
              border: `1px solid ${categoryStyle.color}40`,
              fontSize: '11px',
              fontWeight: '500',
            }}
          >
            {categoryStyle.label}
          </Badge>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-sb-black mb-2 line-clamp-2">
          {flash.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-sb-grey mb-4 line-clamp-3 leading-relaxed">
          {flash.description}
        </p>

        {/* Metadata */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-sb-dark-grey">
            <Calendar size={14} />
            <span>{new Date(flash.date).toLocaleDateString('nl-NL', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-sb-dark-grey">
            <User size={14} />
            <span>{flash.author}</span>
          </div>
          {flash.imagesAvailable > 0 && (
            <div className="flex items-center gap-2 text-xs text-sb-dark-grey">
              <FileText size={14} />
              <span>{flash.imagesAvailable} visualizaties beschikbaar</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {flash.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {flash.tags.slice(0, 4).map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-xs"
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#6B7280',
                  fontSize: '10px',
                  fontWeight: '500',
                }}
              >
                {tag}
              </Badge>
            ))}
            {flash.tags.length > 4 && (
              <Badge
                variant="secondary"
                className="text-xs"
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#6B7280',
                  fontSize: '10px',
                  fontWeight: '500',
                }}
              >
                +{flash.tags.length - 4}
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: palette.background }}>
      <Sidebar activeItem="innoflashes" user={user} />

      <main id="main-content" className="flex-1 overflow-auto">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-8 py-10">
          {/* Header */}
          <header>
            <div className="flex items-center gap-3 mb-2">
              <Lightbulb size={32} style={{ color: palette.accent }} />
              <h1 className="text-3xl font-bold text-sb-black">InnoFlashes</h1>
            </div>
            <p className="text-base text-sb-grey">
              Methodologie-innovaties en data-producten van Roland Kager. Voorbeelden van analyses, datasets, en geospatiale tools ontwikkeld door het data-team.
            </p>
          </header>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: palette.border }}>
              <div className="text-2xl font-bold text-sb-black">{innoflashes.length}</div>
              <div className="text-sm text-sb-grey">InnoFlashes</div>
            </div>
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: palette.border }}>
              <div className="text-2xl font-bold text-sb-black">
                {Object.keys(categoryConfig).length}
              </div>
              <div className="text-sm text-sb-grey">Categorieën</div>
            </div>
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: palette.border }}>
              <div className="text-2xl font-bold text-sb-black">{allTags.length}</div>
              <div className="text-sm text-sb-grey">Tags</div>
            </div>
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: palette.border }}>
              <div className="text-2xl font-bold text-sb-black">2023-2025</div>
              <div className="text-sm text-sb-grey">Tijdspanne</div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg p-6 border" style={{ borderColor: palette.border }}>
            <div className="flex items-center gap-2 mb-4">
              <Filter size={20} style={{ color: palette.accentDark }} />
              <h2 className="text-lg font-semibold text-sb-black">Filters</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-sb-dark-grey mb-2">
                  Categorie
                </label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Alle categorieën" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle categorieën</SelectItem>
                    {Object.entries(categoryConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tag Filter */}
              <div>
                <label className="block text-sm font-medium text-sb-dark-grey mb-2">
                  Tag
                </label>
                <Select value={selectedTag} onValueChange={setSelectedTag}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Alle tags" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle tags</SelectItem>
                    {allTags.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-sb-dark-grey mb-2">
                  Sorteren
                </label>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as any)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sorteren op" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date-desc">Datum (nieuwste eerst)</SelectItem>
                    <SelectItem value="date-asc">Datum (oudste eerst)</SelectItem>
                    <SelectItem value="title">Titel (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Display */}
            {(selectedCategory !== 'all' || selectedTag !== 'all') && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t" style={{ borderColor: palette.border }}>
                <span className="text-sm text-sb-dark-grey">Actieve filters:</span>
                {selectedCategory !== 'all' && (
                  <Badge
                    className="text-xs cursor-pointer"
                    onClick={() => setSelectedCategory('all')}
                    style={{
                      backgroundColor: categoryConfig[selectedCategory as keyof typeof categoryConfig].bgColor,
                      color: categoryConfig[selectedCategory as keyof typeof categoryConfig].color,
                    }}
                  >
                    {categoryConfig[selectedCategory as keyof typeof categoryConfig].label} ×
                  </Badge>
                )}
                {selectedTag !== 'all' && (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer"
                    onClick={() => setSelectedTag('all')}
                    style={{ backgroundColor: palette.background, border: `1px solid ${palette.border}` }}
                  >
                    {selectedTag} ×
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="text-sm text-sb-grey">
            {sortedFlashes.length} {sortedFlashes.length === 1 ? 'resultaat' : 'resultaten'}
          </div>

          {/* InnoFlashes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
            {sortedFlashes.map((flash) => (
              <InnoFlashCard key={flash.id} flash={flash} />
            ))}
          </div>

          {/* Empty State */}
          {sortedFlashes.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-[#E5E7EB]">
              <Lightbulb size={48} className="mx-auto mb-4 text-sb-grey" />
              <h3 className="text-lg font-semibold text-sb-black mb-2">
                Geen InnoFlashes gevonden
              </h3>
              <p className="text-sm text-sb-grey mb-4">
                Probeer andere filters of reset de selectie
              </p>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedTag('all');
                }}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ backgroundColor: palette.accent, color: '#fff' }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      </main>

      {/* InnoFlash Modal */}
      <InnoFlashModal
        flash={selectedFlash}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
